import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationResult } from '@azure/msal-browser';
import { Store } from '@ngrx/store';
import { VyVSharedProductCode } from 'src/app/api/models/vyVSharedProductCode';
import { AuthenticationService } from 'src/app/api/services/authentication.service';
import { PathType } from 'src/app/core/enums/path-type.enum';
import { BackendContext } from 'src/app/core/models/context.model';
import {
  Civilite,
  Devis,
  MontantValeurObjets2000,
  QualiteOccupant,
  Selectionne
} from 'src/app/core/models/form-state.model';
import * as fromSubscriber from 'src/app/core/state/subscriber';
import { MSALInstanceFactory } from 'src/app/shared/config/msal-config';
import { CANAL_VENTE_C } from 'src/app/shared/constants/context.constants';
import {
  CONTEXT,
  MSAL_TOKEN,
  OLD_CONTEXT,
  TOKEN
} from 'src/app/shared/constants/localstorage.constants';
import {
  ADDRESS_PATH,
  ERROR_EMAIL_PATH,
  ETUDIANT_AGE_PATH,
  NOT_FOUND_PATH,
  ROOT_PATH,
  START_PATH
} from 'src/app/shared/constants/route.constants';
import { PRODUCT_PARAM } from 'src/app/shared/constants/url.constants';
import { DistributorCode } from 'src/app/shared/models/enum/distributorCode.enum';
import { EndorsementOverviewService } from 'src/app/shared/services/endorsement-overview.service';
import { PolicyOverviewService } from 'src/app/shared/services/policy-overview.service';
import { QuotationSummaryService } from 'src/app/shared/services/quotation-summary.service';
import { environment } from 'src/environments/environment';
import * as fromContext from '../../../../core/state/context';
import * as fromContract from '../../../../core/state/contract';
import * as fromRoot from '../../../../core/state/core.state';
import * as fromDevis from '../../../../core/state/devis';
import * as fromPath from '../../../../core/state/path';
import * as fromAddress from '../../../../core/state/address';
import { isPartner } from 'src/app/shared/utils/brandUtils';

@Component({
  selector: 'app-redirect',
  templateUrl: './redirect.component.html'
})
export class RedirectComponent implements OnInit {
  pathType: PathType | null = null;
  montantValeurObjets2000: MontantValeurObjets2000 | null = null;
  qualiteOccupant: QualiteOccupant | undefined;
  contextId: string | null = null;
  contextData: BackendContext | null = null;
  quotation: Devis | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private store: Store<fromRoot.State>,
    private quotationSummaryService: QuotationSummaryService,
    private policyOverviewService: PolicyOverviewService,
    private endorsementOverviewService: EndorsementOverviewService,
    private authenticationService: AuthenticationService
  ) {
    if (this.router.url.includes(START_PATH)) {
      const contextValue = this.route.snapshot.paramMap.get(CONTEXT);
      if (contextValue !== null && contextValue !== '') {
        this.contextId = contextValue;
        // check for nested route for new backend context
        const productValue = this.route.snapshot.paramMap.get(PRODUCT_PARAM)?.toUpperCase() || '';
        if (productValue == null || productValue === '') {
          const contextObj = JSON.parse(localStorage.getItem(CONTEXT) || 'null');
          if (contextObj != null) {
            if (localStorage.getItem(OLD_CONTEXT) !== null) {
              this.contextData = JSON.parse(localStorage.getItem(OLD_CONTEXT) || 'null');
              localStorage.setItem(CONTEXT, JSON.stringify(this.contextData));
              localStorage.removeItem(OLD_CONTEXT);
            } else {
              this.contextData = contextObj;
            }
            return;
          } else {
            console.warn('Failed to retrieve context data from localStorage');
          }
        } else {
          this.pathType = productValue as PathType;
        }
      }
    }
  }

  async ngOnInit() {
    const contextObj = JSON.parse(localStorage.getItem(CONTEXT) ?? 'null');
    if (
      contextObj.canalVente === CANAL_VENTE_C &&
      this.distributorUseMsal(contextObj.codeVendeur)
    ) {
      await this.initializeMsal();
    }
    if (
      !contextObj.souscripteur?.email &&
      contextObj.canalVente === CANAL_VENTE_C &&
      this.contextData &&
      environment.brand != 'VYV'
    ) {
      this.router.navigate([ERROR_EMAIL_PATH]);
      return;
    }

    this.store.dispatch(fromContext.setContextId({ contextId: this.contextId! }));
    if (this.contextData != null) {
      // update state using context data
      this.store.dispatch(fromContext.setContextData({ payload: this.contextData }));
      const product =
        this.contextData.produit && this.contextData.produit.includes('_')
          ? this.contextData.produit.split('_')[0]
          : this.contextData.produit;
      switch (product) {
        case VyVSharedProductCode.HM_HC:
        case VyVSharedProductCode.HC:
          this.pathType = PathType.HOUSING;
          break;
        case VyVSharedProductCode.HP:
          this.pathType = PathType.HOUSING_PARIS;
          break;
        case VyVSharedProductCode.HT:
          this.pathType = PathType.HOUSING_TOURCOING;
          break;
        case VyVSharedProductCode.HL:
          this.pathType = PathType.HOUSING_LILLE;
          break;
        case VyVSharedProductCode.HS:
          this.pathType = PathType.HOUSING_SOLIDARITY;
          break;
        case VyVSharedProductCode.HG:
          this.pathType = PathType.HOUSING_GRENOBLE;
          break;
        case VyVSharedProductCode.HR:
          this.pathType = PathType.HOUSING_CRETEIL;
          break;
        case VyVSharedProductCode.HE:
          this.pathType = PathType.HOUSING_ETUDIANTE;
          break;
        case VyVSharedProductCode.HA:
          this.pathType = PathType.HOUSING_MARSEILLE;
          break;
        case VyVSharedProductCode.BS:
          this.pathType = PathType.HOUSING_BAILLEURS_SOCIAUX;
          break;
        default:
          this.pathType = PathType.UNDEFINED;
          break;
      }
    }

    console.log('Using context ID', this.contextId, 'and product', this.pathType);
    // determine where to redirect from context dat

    if (contextObj.partnerCode) {
      this.store.dispatch(fromPath.updatePartnerCode({ partnerCode: contextObj.partnerCode }));
    }

    if (
      this.contextData?.avenants != null &&
      this.contextData.avenants.length > 0 &&
      this.contextData.referenceContrat != null
    ) {
      this.store.dispatch(fromPath.updateType({ pathType: this.pathType || PathType.UNDEFINED }));
      this.authenticationService.refreshToken().subscribe({
        next: token => {
          localStorage.setItem(TOKEN, JSON.stringify(token.accessToken));
        }
      });
      this.endorsementOverviewService.processEndorsementOverview(
        this.contextId as string,
        this.contextData.souscripteur?.email as string,
        this.contextData.contrat?.EstSigne || false,
        this.contextData.avenants
      );
      return;
    } else if (
      this.contextData?.referenceContrat != null &&
      (this.contextData.avenants == null || this.contextData.avenants.length === 0)
    ) {
      this.store.dispatch(fromPath.updateType({ pathType: this.pathType || PathType.UNDEFINED }));
      this.policyOverviewService.processPolicyOverview(
        this.contextData?.referenceContrat,
        this.contextId as string,
        this.contextData.souscripteur?.email as string,
        this.contextData.contrat?.EstSigne,
        this.isQuoteResumedByHmAdvisor()
      );
      return;
    } else if (this.contextData?.referenceDevis != null) {
      // cas de reprise devis
      if (this.pathType) {
        this.store.dispatch(fromPath.updateType({ pathType: this.pathType }));
      }
      this.quotationSummaryService.processQuotationSummary(
        this.contextData.referenceDevis,
        this.contextId!,
        this.contextData.souscripteur?.email ?? ''
      );
      return;
    } else if (this.contextData?.avenants != null && this.contextData?.avenants.length > 0) {
      this.store.dispatch(fromPath.updateType({ pathType: this.pathType || PathType.UNDEFINED }));
      this.endorsementOverviewService.processEndorsementOverview(
        this.contextId as string,
        this.contextData.souscripteur?.email as string,
        this.contextData.contrat?.EstSigne || false,
        this.contextData.avenants
      );
      return;
    } else if (this.pathType) {
      this.store.dispatch(fromPath.updateType({ pathType: this.pathType }));

      switch (this.pathType) {
        case PathType.HOUSING:
        case PathType.HOUSING_PARIS:
        case PathType.HOUSING_TOURCOING:
        case PathType.HOUSING_LILLE:
        case PathType.HOUSING_GRENOBLE:
        case PathType.HOUSING_CRETEIL:
        case PathType.HOUSING_MARSEILLE:
        case PathType.HOUSING_ETUDIANTE:
          if (isPartner(environment.brand)) {
            this.store.dispatch(
              fromSubscriber.patchSubscriber({
                payload: {
                  Civilite:
                    this.contextData?.souscripteur?.civilite === 1
                      ? Civilite.Monsieur
                      : this.contextData?.souscripteur?.civilite === 2
                      ? Civilite.Madame
                      : undefined,
                  Nom: this.contextData?.souscripteur?.nom,
                  Prenom: this.contextData?.souscripteur?.prenom,
                  Email: this.contextData?.souscripteur?.email,
                  DateNaissance: this.contextData?.souscripteur?.dateNaissance,
                  LieuNaissance: this.contextData?.souscripteur?.villeNaissance,
                  PaysNaissance: this.contextData?.souscripteur?.paysNaissance,
                  CodePostalNaissance: this.contextData?.souscripteur?.codePostalNaissance,
                  Nationalite: this.contextData?.souscripteur?.nationalite,
                  Telephone: this.contextData?.souscripteur?.telephone,
                  EstClient: !!this.contextData?.souscripteur
                }
              })
            );
            if (this.contextData?.souscripteur?.adresse) {
              this.store.dispatch(
                fromAddress.patchManuelle({
                  payload: {
                    NumeroEtRue: this.contextData?.souscripteur?.adresse,
                    CodePostal: this.contextData?.souscripteur?.codePostal,
                    Ville: this.contextData?.souscripteur?.ville
                  }
                })
              );
              this.store.dispatch(
                fromAddress.patchSelectedAddress({
                  adresseType: Selectionne.Manuel
                })
              );
            }
          }
          if (this.pathType !== PathType.HOUSING_ETUDIANTE)
            this.router.navigate([`${ROOT_PATH}${ADDRESS_PATH}`]);
          else this.router.navigate([`${ROOT_PATH}${ETUDIANT_AGE_PATH}`]);
          return;
        case PathType.HOUSING_SOLIDARITY:
          this.router.navigate([`${ROOT_PATH}${ADDRESS_PATH}`]);
          return;
        default:
        // fallthrough
      }
    }

    // default 404
    this.router.navigate([`${ROOT_PATH}${NOT_FOUND_PATH}`]);
  }

  private async initializeMsal() {
    const msalInstance = MSALInstanceFactory();
    try {
      await msalInstance.initialize();
      const response = await msalInstance.handleRedirectPromise();
      if (!response) {
        await msalInstance.loginPopup({ scopes: environment.apiConfig.scopes });
      }

      // Attempt to acquire token silently
      const account = msalInstance.getAllAccounts()[0];
      if (account) {
        try {
          const tokenResponse = await msalInstance.acquireTokenSilent({
            scopes: environment.apiConfig.scopes,
            account: account
          });
          this.saveMsalToken(tokenResponse);
          this.store.dispatch(
            fromDevis.patchDevis({
              payload: { ClientAdvisorFullName: tokenResponse.account?.name }
            })
          );
          this.store.dispatch(
            fromContract.patchContract({
              payload: { ClientAdvisorFullName: tokenResponse.account?.name }
            })
          );
        } catch (silentError) {
          console.error(
            'Silent token acquisition failed, falling back to interactive method',
            silentError
          );

          // Fallback to interactive token acquisition
          const tokenResponse = await msalInstance.acquireTokenPopup({
            scopes: environment.apiConfig.scopes
          });
          this.saveMsalToken(tokenResponse);
          this.store.dispatch(
            fromDevis.patchDevis({
              payload: { ClientAdvisorFullName: tokenResponse.account?.name }
            })
          );
          this.store.dispatch(
            fromContract.patchContract({
              payload: { ClientAdvisorFullName: tokenResponse.account?.name }
            })
          );
        }
      }
    } catch (error) {
      console.error('Login error:', error);
    }
  }

  private saveMsalToken(tokenResponse: AuthenticationResult) {
    localStorage.setItem(MSAL_TOKEN, JSON.stringify(tokenResponse.accessToken));
  }

  private distributorUseMsal(codeVendeur: string) {
    return codeVendeur == DistributorCode.VY;
  }

  private isQuoteResumedByHmAdvisor(): boolean {
    return (
      isPartner(environment.brand) &&
      this.contextData?.codeOperation === 'REPRISE_DEVIS' &&
      this.contextData?.modeConnexion === 'APP'
    );
  }
}
